import { Job } from '@applicator/shared'
import { SupabaseJob } from '@applicator/shared'

// Helper function to parse date string
const parseDate = (dateStr: string): number => {
  if (dateStr === 'N/A' || dateStr === 'Add date') return 0
  // Handle format with time: DD/MM/YYYY, HH:MM
  const parts = dateStr.split(',')
  const datePart = parts[0].trim()
  const dateParts = datePart.split('/')
  if (dateParts.length === 3) {
    return new Date(
      parseInt(dateParts[2]),
      parseInt(dateParts[1]) - 1,
      parseInt(dateParts[0])
    ).getTime()
  }
  return 0
}

// Helper function to get verdict value
const getVerdictValue = (supabaseJob: SupabaseJob | undefined): number => {
  if (!supabaseJob) return 0
  if (supabaseJob.relevance_verdict === 'Fit') return 2
  if (supabaseJob.relevance_verdict === 'No Fit') return 1
  return 0
}

// Helper function to get relevance score
const getRelevanceScore = (
  jobId: string,
  supabaseJobs: SupabaseJob[]
): number | null => {
  const supabaseJob = supabaseJobs.find(j => j.id === jobId)
  return supabaseJob?.relevance_score ?? null
}

// Helper function to compare string values
const compareStrings = (
  aValue: string,
  bValue: string,
  sortDirection: 'asc' | 'desc'
): number => {
  const compare = aValue.localeCompare(bValue, 'en', { sensitivity: 'base', numeric: true })
  return sortDirection === 'asc' ? compare : -compare
}

// Helper function to compare number values
const compareNumbers = (
  aValue: number,
  bValue: number,
  sortDirection: 'asc' | 'desc'
): number => {
  return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
}

// Get sort values for different column types
type SortValue = { value: string | number; isString: boolean }

const getSortValue = (
  job: Job,
  sortColumn: string,
  supabaseJobs: SupabaseJob[]
): SortValue | null => {
  switch (sortColumn) {
    case 'Job Position':
      return { value: job.position.toLowerCase().trim(), isString: true }
    case 'Company':
      return { value: job.company.toLowerCase().trim(), isString: true }
    case 'Salary':
      return { value: parseInt(job.salary.replace(/\D/g, '')) || 0, isString: false }
    case 'Location':
      return { value: job.location.toLowerCase().trim(), isString: true }
    case 'Status':
      return { value: job.status.toLowerCase().trim(), isString: true }
    case 'Date Saved':
      return { value: parseDate(job.dateSaved), isString: false }
    case 'Deadline':
      return { value: parseDate(job.deadline), isString: false }
    case 'Date Applied':
      return { value: parseDate(job.dateApplied), isString: false }
    case 'Follow up':
      return { value: job.followUp.toLowerCase().trim(), isString: true }
    case 'Excitement':
      return { value: job.excitement, isString: false }
    case 'Score': {
      const jobId = job.id || ''
      const score = getRelevanceScore(jobId, supabaseJobs)
      return { value: score ?? 0, isString: false }
    }
    case 'Verdict': {
      const jobId = job.id || ''
      const supabaseJob = supabaseJobs.find(j => j.id === jobId)
      return { value: getVerdictValue(supabaseJob), isString: false }
    }
    default:
      return null
  }
}

export const sortJobs = (
  jobsToSort: Job[], 
  sortColumn: string | null, 
  sortDirection: 'asc' | 'desc',
  supabaseJobs: SupabaseJob[] = []
) => {
  if (!sortColumn) return jobsToSort

  const sorted = [...jobsToSort].sort((a, b) => {
    const aSortValue = getSortValue(a, sortColumn, supabaseJobs)
    const bSortValue = getSortValue(b, sortColumn, supabaseJobs)

    if (!aSortValue || !bSortValue) return 0

    // Handle Score column with nulls last
    if (sortColumn === 'Score') {
      const aScore = getRelevanceScore(a.id || '', supabaseJobs)
      const bScore = getRelevanceScore(b.id || '', supabaseJobs)
      
      if (aScore === null || aScore === undefined) return 1
      if (bScore === null || bScore === undefined) return -1
    }

    if (aSortValue.isString && bSortValue.isString) {
      return compareStrings(aSortValue.value as string, bSortValue.value as string, sortDirection)
    }
    
    if (typeof aSortValue.value === 'number' && typeof bSortValue.value === 'number') {
      return compareNumbers(aSortValue.value, bSortValue.value, sortDirection)
    }
    
    return 0
  })

  return sorted
}
